/**
 * MDJ Ritual — Interactive Sliding Puzzle
 * Maison de Jarin / Kaarnika Foods
 *
 * assets/mdj-board.js
 *
 * Owns puzzle state and tile rendering for a single 3x3 board. Generates
 * only solvable layouts by shuffling backward from the solved state via
 * legal moves — this sidesteps parity checks entirely, since any sequence
 * of legal moves from a solved board is by definition reachable and thus
 * solvable.
 *
 * Grid convention:
 *   - 9 positions, indexed 0-8, row = floor(index / 3), col = index % 3.
 *   - this.board[position] = tileId. tileId 8 is the empty cell sentinel.
 *   - this.positionByTile[tileId] = position (inverse of the above, kept
 *     in sync on every swap for O(1) lookups).
 *   - A tile's identity (tileId 0-7) is fixed for its lifetime and maps to
 *     a fixed background-position on its permanent DOM element; only the
 *     element's transform (its position on screen) changes as it moves.
 *
 * Public API consumed by mdj-core.js:
 *   init(), restart(), showHint(), destroy(), setReducedMotion(bool)
 *
 * Public API consumed by mdj-input.js:
 *   getEmptyPosition(), isAdjacentToEmpty(position), moveTileAtPosition(position),
 *   trySlideDirection(direction), pointToPosition(clientX, clientY),
 *   getCellMetrics()
 */

(function () {
  'use strict';

  window.MDJRitual = window.MDJRitual || {};

  var GRID_SIZE = 3;
  var TOTAL_CELLS = GRID_SIZE * GRID_SIZE;
  var EMPTY_TILE = TOTAL_CELLS - 1; // 8

  function rowOf(position) {
    return Math.floor(position / GRID_SIZE);
  }

  function colOf(position) {
    return position % GRID_SIZE;
  }

  function neighborsOf(position) {
    var row = rowOf(position);
    var col = colOf(position);
    var result = [];
    if (row > 0) result.push(position - GRID_SIZE); // up
    if (row < GRID_SIZE - 1) result.push(position + GRID_SIZE); // down
    if (col > 0) result.push(position - 1); // left
    if (col < GRID_SIZE - 1) result.push(position + 1); // right
    return result;
  }

  /**
   * @param {HTMLElement} container - the [data-mdj-board] element.
   * @param {Object} options
   * @param {string} options.imageSrc
   * @param {string} options.imageSrc2x
   * @param {number} options.shuffleMoves
   * @param {number} options.tileGap
   * @param {boolean} options.reducedMotion
   * @param {HTMLElement} [options.hintOverlayEl]
   * @param {Function} options.onReady
   * @param {Function} options.onMove - ({ totalMoves })
   * @param {Function} options.onSolved - ({ moves, timeMs })
   */
  function MDJBoard(container, options) {
    this.container = container;
    this.options = options || {};
    this.reducedMotion = !!this.options.reducedMotion;

    this.board = [];
    this.positionByTile = [];
    this.emptyPosition = EMPTY_TILE;
    this.lastMovedFrom = -1;

    this.tileElements = []; // index = tileId
    this.moveCount = 0;
    this.firstMoveAt = 0;
    this.solved = false;

    this.cellSize = 0;
    this.gap = this.options.tileGap || 3;

    this.imageUrl = this._pickImageSource();

    this._resizeObserver = null;
    this._onResize = this._onResize.bind(this);
    this._hintTimeout = null;
  }

  /* ------------------------------------------------------------------ */
  /* Setup                                                                */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype._pickImageSource = function () {
    var dpr = window.devicePixelRatio || 1;
    if (dpr >= 1.5 && this.options.imageSrc2x) {
      return this.options.imageSrc2x;
    }
    return this.options.imageSrc;
  };

  MDJBoard.prototype.init = function () {
    this._buildSolvedState();
    this._createTileElements();
    this._shuffle(this.options.shuffleMoves || 90);
    this._measure();
    this._layout(false);
    this._updateMovableClasses();

    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(this._onResize);
      this._resizeObserver.observe(this.container);
    } else {
      window.addEventListener('resize', this._onResize);
    }

    // Defer the ready flag one frame so the initial transform values are
    // painted before the opacity/scale transition on .is-ready begins —
    // otherwise tiles can be seen "jumping" into place.
    requestAnimationFrame(
      function () {
        this.container.classList.add('is-ready');
        if (typeof this.options.onReady === 'function') this.options.onReady();
      }.bind(this)
    );
  };

  MDJBoard.prototype._buildSolvedState = function () {
    this.board = [];
    this.positionByTile = [];
    for (var i = 0; i < TOTAL_CELLS; i++) {
      this.board[i] = i;
      this.positionByTile[i] = i;
    }
    this.emptyPosition = EMPTY_TILE;
  };

  MDJBoard.prototype._createTileElements = function () {
    for (var tileId = 0; tileId < EMPTY_TILE; tileId++) {
      var row = rowOf(tileId);
      var col = colOf(tileId);

      var el = document.createElement('div');
      el.className = 'mdj-tile';
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('data-mdj-tile-id', String(tileId));
      el.setAttribute(
        'aria-label',
        'Puzzle tile, position ' + (row + 1) + ', ' + (col + 1)
      );
      el.style.backgroundImage = 'url(' + this.imageUrl + ')';
      el.style.backgroundPosition =
        (col * (100 / (GRID_SIZE - 1))) + '% ' + (row * (100 / (GRID_SIZE - 1))) + '%';

      this.container.appendChild(el);
      this.tileElements[tileId] = el;
    }
  };

  /* ------------------------------------------------------------------ */
  /* Shuffling                                                            */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype._shuffle = function (steps) {
    var lastMoved = -1;

    for (var i = 0; i < steps; i++) {
      var candidates = neighborsOf(this.emptyPosition).filter(function (pos) {
        return pos !== lastMoved;
      });

      if (candidates.length === 0) {
        candidates = neighborsOf(this.emptyPosition);
      }

      var chosen = candidates[Math.floor(Math.random() * candidates.length)];
      this._swap(chosen, this.emptyPosition);
      lastMoved = this.emptyPosition;
      this.emptyPosition = chosen;
    }
  };

  MDJBoard.prototype._swap = function (posA, posB) {
    var tileA = this.board[posA];
    var tileB = this.board[posB];
    this.board[posA] = tileB;
    this.board[posB] = tileA;
    this.positionByTile[tileA] = posB;
    this.positionByTile[tileB] = posA;
  };

  /* ------------------------------------------------------------------ */
  /* Layout / measurement                                                 */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype._measure = function () {
    var rect = this.container.getBoundingClientRect();
    var size = Math.min(rect.width, rect.height);
    this.cellSize = (size - this.gap * (GRID_SIZE - 1)) / GRID_SIZE;
  };

  MDJBoard.prototype._layout = function (animate) {
    for (var tileId = 0; tileId < EMPTY_TILE; tileId++) {
      this._placeTile(tileId, animate);
    }
  };

  MDJBoard.prototype._placeTile = function (tileId, animate) {
    var el = this.tileElements[tileId];
    if (!el) return;

    var position = this.positionByTile[tileId];
    var row = rowOf(position);
    var col = colOf(position);

    var x = col * (this.cellSize + this.gap);
    var y = row * (this.cellSize + this.gap);

    el.style.width = this.cellSize + 'px';
    el.style.height = this.cellSize + 'px';
    el.style.transform = 'translate3d(' + x + 'px, ' + y + 'px, 0)';
    el.setAttribute('data-position', String(position));

    if (animate && !this.reducedMotion) {
      el.classList.add('mdj-tile--settling');
      window.setTimeout(
        function () {
          el.classList.remove('mdj-tile--settling');
        },
        400 * (this.options.speedMultiplier || 1)
      );
    }
  };

  MDJBoard.prototype._onResize = function () {
    this._measure();
    this._layout(false);
  };

  /* ------------------------------------------------------------------ */
  /* Movable state                                                        */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype._updateMovableClasses = function () {
    for (var tileId = 0; tileId < EMPTY_TILE; tileId++) {
      var el = this.tileElements[tileId];
      if (!el) continue;
      var position = this.positionByTile[tileId];
      if (this.isAdjacentToEmpty(position)) {
        el.classList.add('mdj-tile--movable');
      } else {
        el.classList.remove('mdj-tile--movable');
      }
    }
  };

  /* ------------------------------------------------------------------ */
  /* Public movement API (consumed by mdj-input.js)                       */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype.getEmptyPosition = function () {
    return this.emptyPosition;
  };

  MDJBoard.prototype.isAdjacentToEmpty = function (position) {
    if (position === this.emptyPosition) return false;
    return neighborsOf(this.emptyPosition).indexOf(position) !== -1;
  };

  MDJBoard.prototype.moveTileAtPosition = function (position) {
    if (this.solved) return false;
    if (!this.isAdjacentToEmpty(position)) return false;

    var movingTileId = this.board[position];
    this._swap(position, this.emptyPosition);
    this.emptyPosition = position;

    this._placeTile(movingTileId, true);
    this._updateMovableClasses();

    this.moveCount += 1;
    if (this.moveCount === 1) {
      this.firstMoveAt = performance.now();
    }

    if (typeof this.options.onMove === 'function') {
      this.options.onMove({ totalMoves: this.moveCount });
    }

    this._checkSolved();

    return true;
  };

  /**
   * Maps an arrow-key direction to the tile that must move, per the
   * standard sliding-puzzle convention: the direction describes which
   * way the EMPTY cell appears to move, so the tile adjacent to the
   * empty cell on the opposite side is the one that slides.
   */
  MDJBoard.prototype.trySlideDirection = function (direction) {
    var row = rowOf(this.emptyPosition);
    var col = colOf(this.emptyPosition);
    var targetPosition = -1;

    switch (direction) {
      case 'up':
        if (row < GRID_SIZE - 1) targetPosition = this.emptyPosition + GRID_SIZE;
        break;
      case 'down':
        if (row > 0) targetPosition = this.emptyPosition - GRID_SIZE;
        break;
      case 'left':
        if (col < GRID_SIZE - 1) targetPosition = this.emptyPosition + 1;
        break;
      case 'right':
        if (col > 0) targetPosition = this.emptyPosition - 1;
        break;
      default:
        return false;
    }

    if (targetPosition === -1) return false;
    return this.moveTileAtPosition(targetPosition);
  };

  /**
   * Given viewport coordinates, returns the grid position they fall
   * within, or -1 if outside the board. Used by mdj-input.js to resolve
   * both tap targets and drag/swipe destinations.
   */
  MDJBoard.prototype.pointToPosition = function (clientX, clientY) {
    var rect = this.container.getBoundingClientRect();
    var x = clientX - rect.left;
    var y = clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return -1;

    var step = this.cellSize + this.gap;
    var col = Math.min(GRID_SIZE - 1, Math.floor(x / step));
    var row = Math.min(GRID_SIZE - 1, Math.floor(y / step));

    return row * GRID_SIZE + col;
  };

  MDJBoard.prototype.getCellMetrics = function () {
    return { cellSize: this.cellSize, gap: this.gap, gridSize: GRID_SIZE };
  };

  MDJBoard.prototype.getTileElementAtPosition = function (position) {
    var tileId = this.board[position];
    if (tileId === EMPTY_TILE) return null;
    return this.tileElements[tileId];
  };

  MDJBoard.prototype.getPositionOfElement = function (el) {
    var attr = el.getAttribute('data-position');
    return attr === null ? -1 : parseInt(attr, 10);
  };

  /* ------------------------------------------------------------------ */
  /* Completion                                                           */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype._checkSolved = function () {
    for (var i = 0; i < TOTAL_CELLS; i++) {
      if (this.board[i] !== i) return;
    }

    this.solved = true;
    this.container.classList.add('is-solved');

    var timeMs = this.firstMoveAt ? performance.now() - this.firstMoveAt : 0;

    if (typeof this.options.onSolved === 'function') {
      this.options.onSolved({ moves: this.moveCount, timeMs: timeMs });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Hint                                                                 */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype.showHint = function () {
    var overlay = this.options.hintOverlayEl;
    if (!overlay) return;

    overlay.style.backgroundImage = 'url(' + this.imageUrl + ')';
    overlay.hidden = false;

    // Force layout so the opacity transition triggers even if called
    // repeatedly in quick succession.
    // eslint-disable-next-line no-unused-expressions
    overlay.offsetHeight;
    overlay.classList.add('is-visible');

    if (this._hintTimeout) window.clearTimeout(this._hintTimeout);

    var duration = this.reducedMotion ? 900 : 1400;
    this._hintTimeout = window.setTimeout(
      function () {
        overlay.classList.remove('is-visible');
        window.setTimeout(function () {
          overlay.hidden = true;
        }, this.reducedMotion ? 0 : 300);
      }.bind(this),
      duration
    );
  };

  /* ------------------------------------------------------------------ */
  /* Restart                                                              */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype.restart = function () {
    this.solved = false;
    this.moveCount = 0;
    this.firstMoveAt = 0;
    this.container.classList.remove('is-solved');

    this._buildSolvedState();
    this._shuffle(this.options.shuffleMoves || 90);
    this._measure();
    this._layout(true);
    this._updateMovableClasses();
  };

  /* ------------------------------------------------------------------ */
  /* Reduced motion                                                       */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype.setReducedMotion = function (value) {
    this.reducedMotion = !!value;
  };

  /* ------------------------------------------------------------------ */
  /* Cleanup                                                              */
  /* ------------------------------------------------------------------ */

  MDJBoard.prototype.destroy = function () {
    if (this._hintTimeout) window.clearTimeout(this._hintTimeout);

    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    } else {
      window.removeEventListener('resize', this._onResize);
    }

    for (var i = 0; i < this.tileElements.length; i++) {
      var el = this.tileElements[i];
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }
    this.tileElements = [];
  };

  window.MDJRitual.Board = MDJBoard;
})();